import React from "react";

import { getInstallType, InstallTypes } from "../utils/getInstallType";

import {
  CustomCssContext,
  CustomCssContextEmpty,
  type CustomCssContextType,
} from "./CustomCssContext";

// This is a workaround to allow the use of css variables in styled-components
declare module "react" {
  interface CSSProperties {
    [key: `--${string}`]: string | number;
  }
}

export type VerificationFormLayout = "fullRight" | "center" | "splitRight";
export interface ThemeChoices {
  logoUrl?: string;
  font?: string;
  backgroundColor?: string;
  primaryFontColor?: string;
  buttonColor?: string;
  buttonFontColor?: string;
  linkColor?: string;
  h1FontColor?: string;
  helperFontColor?: string;
  customCss?: string; // this is only customer-provided CSS, not the full CSS built by the program builder
  landingPage: {
    logoUrl?: string;
    backgroundImageUrl?: string;
    backgroundColor?: string;
    primaryFontColor?: string;
    layout?: VerificationFormLayout;
  };
}

interface CustomCssContextProviderProps {
  themeChoices?: ThemeChoices;
  customCssFromOptions?: string;
  children?: React.ReactNode;
}

export const themeChoiceVars: Record<keyof CustomCssContextType, string | null> = {
  landingBackgroundColor: "--sheerid-landing-background-color",
  landingPrimaryFontColor: "--sheerid-landing-primary-font-color",
  landingBackgroundImage: "--sheerid-landing-background-image-url",
  formBackgroundColor: "--sheerid-color-bg",
  formPrimaryFontColor: "--sheerid-color-fg",
  formButtonColor: "--sheerid-form-button-color",
  formButtonFontColor: "--sheerid-form-button-font-color",
  formLinkColor: "--sheerid-form-link-color",
  formH1FontColor: "--sheerid-form-h1-font-color",
  formHelperFontColor: "--sheerid-form-helper-font-color",
  formFont: "--sheerid-font-family-body",

  cssVariables: null,
  customStyles: null,
} as const;

export const generatedCssVariables = {
  "--sheerid-color-fg-05": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 5,
  },
  "--sheerid-color-fg-10": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 10,
  },
  "--sheerid-color-fg-20": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 20,
  },
  "--sheerid-color-fg-30": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 30,
  },
  "--sheerid-color-fg-40": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 40,
  },
  "--sheerid-color-fg-50": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 50,
  },
  "--sheerid-color-fg-60": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 60,
  },
  "--sheerid-color-fg-70": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 70,
  },
  "--sheerid-color-fg-80": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 80,
  },
  "--sheerid-color-fg-90": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 90,
  },
  "--sheerid-color-fg-t05": {
    from: "transparent",
    to: "--sheerid-color-fg",
    percentage: 5,
  },
  "--sheerid-color-fg-t10": {
    from: "transparent",
    to: "--sheerid-color-fg",
    percentage: 10,
  },
  "--sheerid-color-fg-t20": {
    from: "transparent",
    to: "--sheerid-color-fg",
    percentage: 20,
  },
  "--sheerid-color-fg-t30": {
    from: "transparent",
    to: "--sheerid-color-fg",
    percentage: 30,
  },
  "--sheerid-color-fg-t40": {
    from: "transparent",
    to: "--sheerid-color-fg",
    percentage: 40,
  },
  "--sheerid-color-fg-t50": {
    from: "transparent",
    to: "--sheerid-color-fg",
    percentage: 50,
  },
  "--sheerid-color-fg-t60": {
    from: "transparent",
    to: "--sheerid-color-fg",
    percentage: 60,
  },
  "--sheerid-color-fg-t70": {
    from: "transparent",
    to: "--sheerid-color-fg",
    percentage: 70,
  },
  "--sheerid-color-fg-t80": {
    from: "transparent",
    to: "--sheerid-color-fg",
    percentage: 80,
  },
  "--sheerid-color-fg-t90": {
    from: "transparent",
    to: "--sheerid-color-fg",
    percentage: 90,
  },
  "--sheerid-color-warning": {
    from: "--sheerid-color-fg",
    to: "#cc8800",
    percentage: 60,
  },
  "--sheerid-color-bg-alternate": {
    from: "--sheerid-color-bg",
    to: "--sheerid-color-fg",
    percentage: 3,
  },
} as const;

const CustomCssContextProvider: React.FC<CustomCssContextProviderProps> = ({
  themeChoices,
  customCssFromOptions,
  children,
}) => {
  const removePollutingStyles = (styles: string): string => {
    if (window.self === window.top) {
      return styles
        .replace(/body,/g, "")
        .replace(/label,/g, "")
        .replace(/button\[type=('|")submit('|")\]\s*?\{[\s\S]*?\}\n?/g, "") // button type submit overwrites
        .replace(/a:link,\s+a:visited,\s+a:hover,\s+a:active\s*?\{[\s\S]*?\}\n?/g, "") // links overwrites
        .replace(/h1\s*?\{[\s\S]*?\}\n?/g, ""); // h1 overwrites
    }
    return styles;
  };

  const styles = customCssFromOptions || themeChoices?.customCss || "";
  const customStyles = styles ? removePollutingStyles(styles) : null;

  const landingPage = themeChoices?.landingPage;

  const landingBackgroundColor =
    landingPage?.backgroundColor || CustomCssContextEmpty.landingBackgroundColor;
  const landingPrimaryFontColor =
    landingPage?.primaryFontColor || CustomCssContextEmpty.landingPrimaryFontColor;
  const landingBackgroundImage =
    landingPage?.backgroundImageUrl || CustomCssContextEmpty.landingBackgroundImage;

  const formBackgroundColor =
    themeChoices?.backgroundColor || CustomCssContextEmpty.formBackgroundColor;
  const formPrimaryFontColor =
    themeChoices?.primaryFontColor || CustomCssContextEmpty.formPrimaryFontColor;
  const formButtonColor = themeChoices?.buttonColor || CustomCssContextEmpty.formButtonColor;
  const formButtonFontColor =
    themeChoices?.buttonFontColor || CustomCssContextEmpty.formButtonFontColor;
  const formLinkColor = themeChoices?.linkColor || CustomCssContextEmpty.formLinkColor;
  const formH1FontColor = themeChoices?.h1FontColor || CustomCssContextEmpty.formH1FontColor;
  const formHelperFontColor =
    themeChoices?.helperFontColor || CustomCssContextEmpty.formHelperFontColor;
  const formFont = themeChoices?.font || CustomCssContextEmpty.formFont;

  const cssVariables = {
    [themeChoiceVars.landingBackgroundColor]: landingBackgroundColor,
    [themeChoiceVars.landingPrimaryFontColor]: landingPrimaryFontColor,
    [themeChoiceVars.landingBackgroundImage]:
      landingBackgroundImage === CustomCssContextEmpty.landingBackgroundImage
        ? CustomCssContextEmpty.landingBackgroundImage
        : `url(${landingBackgroundImage})`,
    [themeChoiceVars.formBackgroundColor]: formBackgroundColor,
    [themeChoiceVars.formPrimaryFontColor]: formPrimaryFontColor,
    [themeChoiceVars.formButtonColor]: formButtonColor,
    [themeChoiceVars.formButtonFontColor]: formButtonFontColor,
    [themeChoiceVars.formLinkColor]: formLinkColor,
    [themeChoiceVars.formH1FontColor]: formH1FontColor,
    [themeChoiceVars.formHelperFontColor]: formHelperFontColor,
    [themeChoiceVars.formFont]:
      formFont === CustomCssContextEmpty.formFont
        ? CustomCssContextEmpty.formFont
        : `${formFont}, var(--sheerid-font-family-sans)`,
    ...Object.entries(generatedCssVariables).reduce(
      (acc, [key, value]) => {
        const from = value.from.startsWith("--") ? `var(${value.from})` : value.from;
        const to = value.to.startsWith("--") ? `var(${value.to})` : value.to;

        acc[key] =
          `color-mix(in oklab, ${from} ${100 - value.percentage}%, ${to} ${value.percentage}%)`;
        return acc;
      },
      {} as Record<string, string>,
    ),
  };

  const container = React.useRef<HTMLDivElement>(null);
  const isInIframe = getInstallType() === InstallTypes.cdnInlineIframe;
  const isModal = getInstallType() === InstallTypes.cdnModalIframe;

  return (
    <CustomCssContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        landingBackgroundColor,
        landingPrimaryFontColor,
        landingBackgroundImage,
        formBackgroundColor,
        formPrimaryFontColor,
        formButtonColor,
        formButtonFontColor,
        formLinkColor,
        formH1FontColor,
        formHelperFontColor,
        formFont,

        cssVariables,
        customStyles,
      }}
    >
      <div style={cssVariables} ref={container} data-in-iframe={isInIframe} data-in-modal={isModal}>
        {customStyles && <style>{customStyles}</style>}

        {children}
      </div>
    </CustomCssContext.Provider>
  );
};

export default CustomCssContextProvider;
